import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import milestoneActive from "../../../assets/Images/TempleDetail/mobile/milestone-active.svg";
import milestone from "../../../assets/Images/TempleDetail/mobile/milestone.svg";
import { T, FONT, Photo } from "../shared";
import { MobileCard, MobileHeading } from "./shared";

// Figma "Mobile revamp" 739:17365 — Temple history: the pinned photo with its caption and era
// thumbnails, and a 235px timeline window with the progress rail and a red scrollbar thumb.
// The timeline doesn't scroll on its own: when the section reaches the top it pins, page scrolling
// moves through the eras, and after the last one the page carries on.

const PIN_TOP = 12; // gap above the pinned panel
const PHOTO_H = 335; // photo (design); shrinks only when the phone is too short for heading + photo + timeline
const PHOTO_MIN = 170;
const BOTTOM_BAR = 66; // the page's fixed "Donate Now" bar (58px) and a little air
const WINDOW_H = 235; // timeline window (design minimum; grows on tall phones so no blank card shows under it)
const THUMB_H = 72;
const STEP_VH = 30; // page scroll per era while pinned (was 55 — felt slow on phones)

const MobileHistory = ({ history }) => {
  const eras = history?.eras || [];
  const trackRef = useRef(null);
  const panelRef = useRef(null);
  const listRef = useRef(null);
  const windowRef = useRef(null);
  const eraRefs = useRef([]);
  const thumbRef = useRef(null);
  // Scroll position drives the timeline and the red thumb straight on the DOM (no React render per
  // frame — a re-render there made MUI write a new CSS class every frame and the page stuttered on
  // phones). React only re-renders when the active era changes.
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  // Photo crossfade: only the current and the previous era's photo are mounted (each has a blurred
  // fill layer, and four of those stacked in the pinned panel were heavy to composite on phones).
  const [prevActive, setPrevActive] = useState(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const pinned = eras.length > 1;
  const show = (idx) => {
    if (idx === activeRef.current) return;
    setPrevActive(activeRef.current);
    activeRef.current = idx;
    setActive(idx);
  };

  useEffect(() => {
    const panel = panelRef.current;
    if (!pinned || !panel) return undefined;
    const ro = new ResizeObserver(() => setPanelHeight(panel.offsetHeight));
    ro.observe(panel);
    return () => ro.disconnect();
  }, [pinned]);

  useEffect(() => {
    if (!pinned) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const track = trackRef.current;
      const list = listRef.current;
      if (!track || !list || !panelRef.current) return;
      const range = track.offsetHeight - panelRef.current.offsetHeight;
      const scrolled = Math.min(Math.max(-track.getBoundingClientRect().top + PIN_TOP, 0), range);
      const p = range > 0 ? scrolled / range : 0;
      const winH = windowRef.current?.offsetHeight || WINDOW_H;
      const max = Math.max(0, list.scrollHeight - winH);
      const y = p * max;
      // active era: the last one whose top has passed the upper third of the window
      let idx = 0;
      eraRefs.current.forEach((el, i) => {
        if (el && el.offsetTop <= y + winH * 0.33) idx = i;
      });
      if (p > 0.97) idx = eras.length - 1;
      list.style.transform = `translate3d(0, ${-y}px, 0)`;
      if (thumbRef.current) thumbRef.current.style.transform = `translate3d(0, ${p * (winH - 24 - THUMB_H)}px, 0)`;
      show(idx);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [pinned, eras.length, panelHeight]);

  // Thumbnail tap: scroll the page to that era.
  const goTo = (i) => {
    const track = trackRef.current;
    const list = listRef.current;
    const el = eraRefs.current[i];
    if (!pinned || !track || !list || !el) return show(i);
    const range = track.offsetHeight - panelRef.current.offsetHeight;
    const max = Math.max(1, list.scrollHeight - (windowRef.current?.offsetHeight || WINDOW_H));
    const p = Math.min(1, el.offsetTop / max);
    window.scrollTo({ top: track.getBoundingClientRect().top + window.scrollY - PIN_TOP + p * range, behavior: "smooth" });
  };

  if (!eras.length) return null;
  const current = eras[active] || eras[0];

  const panel = (
    <Box
      ref={panelRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        background: "#FFFFFF",
        // pinned: heading + photo + timeline stay on screen above the donate bar
        // pinned: the panel is exactly the screen above the donate bar, so nothing blank shows under it
        height: pinned ? `calc(100svh - ${PIN_TOP + BOTTOM_BAR}px)` : "auto",
        minHeight: pinned ? `${PHOTO_MIN + WINDOW_H + 140}px` : 0,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <MobileHeading eyebrow="Read the history" hindi="॥ इतिहास ॥" title={history.title} />
      </Box>
      {/* Pinned photo — Figma "Mobile revamp" 739:17365: the photo fills the 335px frame (cover), the
          counter pill top-left, and the caption with the era thumbnails over a dark fade at the bottom */}
      <Box
        sx={{
          position: "relative",
          flex: `0 1 ${PHOTO_H}px`,
          height: `${PHOTO_H}px`,
          minHeight: `${PHOTO_MIN}px`,
          borderRadius: "20px",
          overflow: "hidden",
          background: T.maroon,
          isolation: "isolate",
        }}
      >
        {/* previous photo underneath, current one fades in on top */}
        {eras.map((era, i) =>
          i === active || i === prevActive ? (
            <Box
              key={i}
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: i === active ? 1 : 0,
                ...(i === active && prevActive !== null && { animation: "thfEraFade 0.45s ease both" }),
                "@keyframes thfEraFade": { from: { opacity: 0 }, to: { opacity: 1 } },
              }}
            >
              <Photo src={era.image} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </Box>
          ) : null
        )}
        <Box sx={{ position: "absolute", inset: 0, zIndex: 2, background: "linear-gradient(180deg, rgba(26,5,8,0.35) 0%, rgba(26,5,8,0) 30%, rgba(36,5,8,0.25) 62%, rgba(36,5,8,0.95) 100%)" }} />
        <Box sx={{ position: "absolute", zIndex: 3, left: "16px", top: "16px", px: "12px", py: "6px", borderRadius: "100px", background: "rgba(26,8,8,0.55)" }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "11px", lineHeight: "normal", color: "#FFFFFF" }}>
            {String(active + 1).padStart(2, "0")} / {String(eras.length).padStart(2, "0")}
          </Typography>
        </Box>
        <Box sx={{ position: "absolute", zIndex: 3, left: "20px", right: "20px", bottom: "12px", display: "flex", flexDirection: "column", gap: "5px" }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "12px", lineHeight: 1.5, color: T.goldLight, textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>{current.caption}</Typography>
          <Box sx={{ display: "flex", gap: "7px", pt: "5px", alignItems: "flex-start" }}>
            {eras.map((era, i) => {
              const on = i === active;
              return (
                <Box
                  key={i}
                  component="button"
                  type="button"
                  aria-label={`Show ${era.caption || era.year || `era ${i + 1}`}`}
                  onClick={() => goTo(i)}
                  sx={{
                    width: on ? "40px" : "32px",
                    height: "30px",
                    p: 0,
                    flexShrink: 0,
                    borderRadius: "7px",
                    border: on ? `2px solid ${T.red}` : "1px solid rgba(255,255,255,0.5)",
                    opacity: on ? 1 : 0.7,
                    overflow: "hidden",
                    background: T.maroon,
                    boxSizing: "border-box",
                    cursor: "pointer",
                    transition: "width 0.2s, opacity 0.2s",
                    "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "2px" },
                  }}
                >
                  <Photo src={era.image} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Timeline window */}
      <Box ref={windowRef} sx={{ position: "relative", flex: pinned ? `1 0 ${WINDOW_H}px` : "none", minHeight: 0, overflow: "hidden", pr: "18px" }} aria-live="polite">
        <Box
          ref={listRef}
          sx={{ display: "flex", flexDirection: "column", willChange: "transform" }}
        >
          {eras.map((era, i) => {
            const on = i === active;
            const last = i === eras.length - 1;
            return (
              <Box key={i} ref={(el) => (eraRefs.current[i] = el)} sx={{ display: "flex", gap: "13px", alignItems: "stretch", opacity: on ? 1 : 0.4, transition: "opacity 0.35s ease" }}>
                <Box sx={{ width: "15px", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Box component="img" src={on ? milestoneActive : milestone} alt="" sx={{ width: on ? "12px" : "9px", height: on ? "12px" : "9px", mt: on ? 0 : "1.5px", flexShrink: 0 }} />
                  <Box sx={{ flex: 1, width: "1px", background: "#EFE4D3" }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px", pb: last ? 0 : "28px" }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "9px", lineHeight: 1.5, color: on ? T.red : T.gold, whiteSpace: "pre-wrap" }}>{era.tag}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "34px", lineHeight: 1.1, color: era.accent ? T.red : T.gold }}>{era.year}</Typography>
                  <Typography component="h3" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "18px", lineHeight: 1.5, color: T.ink }}>{era.title}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: "13px", lineHeight: 1.5, color: "#4A4542" }}>{era.body}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        {pinned && (
          <Box aria-hidden="true" sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "3px", borderRadius: "100px", background: "#EFE4D3" }}>
            <Box ref={thumbRef} sx={{ position: "absolute", left: 0, top: "12px", width: "3px", height: `${THUMB_H}px`, borderRadius: "100px", background: "#CC2926", willChange: "transform" }} />
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <MobileCard id="history">
      {pinned ? (
        <Box ref={trackRef} sx={{ position: "relative", height: panelHeight ? `calc(${panelHeight}px + ${eras.length * STEP_VH}svh)` : "auto" }}>
          <Box sx={{ position: "sticky", top: `${PIN_TOP}px` }}>{panel}</Box>
        </Box>
      ) : (
        panel
      )}
    </MobileCard>
  );
};

export default MobileHistory;
